import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import Card from "components/Card";
import Button from "components/Button";
import BillingCheckbox from "../components/BillingCheckbox";
import Toaster from "components/Toaster";
import { fmtNumber } from "utils";
import Messages from "misc/Messages";
import { COLORS } from "../constants";
import { useNextHigherPlan } from "v2/hooks/billing/pricing-options/useNextHigherPlan";
import { useBillingNavigation } from "../hooks/useBillingNavigation";
import { useToasterFromActionData } from "hooks/useToasterFromActionData";
import { useCallbackAfterSubmission } from "v2/hooks/useCallbackAfterSubmission";
import LoadingCard from "../components/LoadingCard";
import RecommendedBadge from "../components/RecommendedBadge";
import PayAsYouGoInlineControls, { PaygControlsRef } from "./PayAsYouGoInlineControls";
import {
  trackBillingV2AutoUpgradeChangeInitiated,
  trackBillingV2AutoUpgradeChangeSucceeded,
  trackBillingV2PaygChangeInitiated,
  trackBillingV2PaygChangeSubmitted,
  trackBillingV2PaygChangeSucceeded,
} from "utils/Tracking";
import { useActiveSubscription } from "routes/data-routes/billing-data/active-subscription";
import { usePricingOptions } from "routes/data-routes/billing-data/pricing-options";
import { useAutoUpgradePrompt } from "routes/data-routes/billing-data/pricing-options/hooks/useAutoUpgradePrompt";


type LimitOption = "interrupted" | "auto-upgrade" | "payg";

interface LimitOptionConfig {
  id: LimitOption;
  title: string;
  description: string;
  badge?: string;
}

interface RadioOptionProps {
  isSelected: boolean;
  onClick: () => void;
  title: string;
  description: string;
  badge?: string;
}

const RadioOption = ({
  isSelected,
  onClick,
  title,
  description,
  badge,
}: RadioOptionProps) => {
  const cardBgColor = isSelected ? "bg-blue-50" : "";
  const cardBorderColor = isSelected ? COLORS.PRIMARY_BLUE : undefined;

  return (
    <button
      onClick={onClick}
      className={`flex items-start gap-2 p-4 border-2 rounded-lg text-left transition-all relative ${cardBgColor}`}
      style={{ borderColor: cardBorderColor }}
    >
      {badge && (
        <RecommendedBadge className="absolute top-3 right-3" />
      )}

      <div className="flex-shrink-0 mt-0.5">
        <BillingCheckbox isSelected={isSelected} />
      </div>

      <div className="flex-1">
        <h4 className="font-medium text-gray-900 mb-1">
          {title}
        </h4>
        <p className="text-sm text-gray-600">
          {description}
        </p>
      </div>
    </button>
  );
};

export default function LimitHandlingCard() {
  const { activeSubscription } = useActiveSubscription();
  const { nextHigherPlan, isLoading } = useNextHigherPlan();
  const { navigate } = useBillingNavigation();
  const { pricingOptions, pricingOptionsFetcher } = usePricingOptions();
  const autoUpgradePrompt = useAutoUpgradePrompt();
  const paygControlsRef = useRef<PaygControlsRef>(null);

  // pricingOptionsFetcher is shared across auto-upgrade and PAYG actions; this ref
  // attributes the success event to the originating action.
  const pendingPricingActionRef = useRef<
    | { kind: 'auto_upgrade'; enabled: boolean; source: string }
    | { kind: 'payg'; enabled: boolean; source: string }
    | null
  >(null);

  useToasterFromActionData(pricingOptionsFetcher);

  useCallbackAfterSubmission(() => {
    paygControlsRef.current?.reset();
    const pending = pendingPricingActionRef.current;
    if (pending) {
      if (pending.kind === 'auto_upgrade') {
        trackBillingV2AutoUpgradeChangeSucceeded({ enabled: pending.enabled, source: pending.source });
      } else {
        trackBillingV2PaygChangeSucceeded({ enabled: pending.enabled, source: pending.source });
      }
      pendingPricingActionRef.current = null;
    }
  }, pricingOptionsFetcher);

  // Determine initial state based on what's currently enabled from backend
  const [selectedOption, setSelectedOption] = useState<LimitOption>(() => {
    if (pricingOptions?.["auto-upgrade"]?.enabled) return "auto-upgrade";
    if (pricingOptions?.["pay-as-you-go"]?.enabled) return "payg";
    return "interrupted";
  });

  // Update selected option when pricing options change
  const autoUpgradeEnabled = pricingOptions?.["auto-upgrade"]?.enabled;
  const paygEnabled = pricingOptions?.["pay-as-you-go"]?.enabled;

  useEffect(() => {
    if (autoUpgradeEnabled) {
      setSelectedOption("auto-upgrade");
    } else if (paygEnabled) {
      setSelectedOption("payg");
    } else if (pricingOptions) {
      setSelectedOption("interrupted");
    }
  }, [autoUpgradeEnabled, paygEnabled, pricingOptions]);

  // Determine current backend state for comparison
  const currentBackendOption: LimitOption = autoUpgradeEnabled
    ? "auto-upgrade"
    : paygEnabled
      ? "payg"
      : "interrupted";

  // PAYG inline controls — delegated to PayAsYouGoInlineControls component via ref
  const handleOptionSelect = (option: LimitOption) => {
    if (option !== "payg") {
      paygControlsRef.current?.reset();
    }
    setSelectedOption(option);
  };

  // PAYG state from child component (updated via callback)
  const [paygHasChanges, setPaygHasChanges] = useState(false);
  const [paygIsValid, setPaygIsValid] = useState(false);

  const handlePaygStateChange = useCallback((hasChanges: boolean, isValid: boolean) => {
    setPaygHasChanges(hasChanges);
    setPaygIsValid(isValid);
  }, []);

  // Track changes
  const hasOptionChange = selectedOption !== currentBackendOption;
  const hasChanges = hasOptionChange || paygHasChanges;

  // Build available secondary options based on backend flags
  // NOTE: Backend determines which options are available per plan type:
  // - Legacy annual_* plans (being phased out) → disableNewPricingOptions() applied → NO auto-upgrade/PAYG
  // - New yearly_* plans → auto-upgrade/PAYG available
  // - Both display as "Annual" on frontend for UX consistency
  const secondaryOptions = useMemo((): LimitOptionConfig[] => {
    const options: LimitOptionConfig[] = [];

    const autoUpgradeDescription =
      activeSubscription?.apiCreditLimit && nextHigherPlan?.planName
        ? `When you hit your ${fmtNumber(activeSubscription.apiCreditLimit)} request limit, move to the ${nextHigherPlan.planName} Plan automatically.`
        : "When you hit your request limit, move to the next plan automatically.";

    const paygDescription = Messages.billingV2.payg.description;

    // Add auto-upgrade if allowed
    if (pricingOptions?.["auto-upgrade"]?.allowed) {
      options.push({
        id: "auto-upgrade",
        title: autoUpgradePrompt,
        description: autoUpgradeDescription,
      });
    }

    // Add PAYG if allowed
    if (pricingOptions?.["pay-as-you-go"]?.allowed) {
      options.push({
        id: "payg",
        title: "Pay-as-you-go",
        description: paygDescription,
      });
    }

    return options;
  }, [
    pricingOptions,
    activeSubscription?.apiCreditLimit,
    nextHigherPlan?.planName,
    autoUpgradePrompt,
  ]);

  const handleSaveChanges = () => {
    const wasAutoUpgradeEnabled = autoUpgradeEnabled;
    const wasPaygEnabled = paygEnabled;

    // If user selected interrupted
    if (selectedOption === "interrupted") {
      // If auto-upgrade was enabled, show disable modal
      if (wasAutoUpgradeEnabled) {
        trackBillingV2AutoUpgradeChangeInitiated({ enabled: false, source: 'limit_handling_card' });
        pendingPricingActionRef.current = { kind: 'auto_upgrade', enabled: false, source: 'limit_handling_card' };
        navigate("plans/auto-upgrade/disable", { viewTransition: true });
        return;
      }

      // If PAYG was enabled, show change/disable modal
      if (wasPaygEnabled) {
        trackBillingV2PaygChangeInitiated({ enabled: false, source: 'limit_handling_card' });
        pendingPricingActionRef.current = { kind: 'payg', enabled: false, source: 'limit_handling_card' };
        navigate("plans/pay-as-you-go/change", { viewTransition: true });
        return;
      }

      return;
    }

    // For auto-upgrade
    if (selectedOption === "auto-upgrade") {
      if (wasAutoUpgradeEnabled) {
        // Already enabled, just show toast
        Toaster.info(
          "Already Enabled",
          "Auto-upgrade is already enabled for your account.",
        );
      } else {
        // Navigate to enable modal (handles payment method gating internally)
        trackBillingV2AutoUpgradeChangeInitiated({ enabled: true, source: 'limit_handling_card' });
        pendingPricingActionRef.current = { kind: 'auto_upgrade', enabled: true, source: 'limit_handling_card' };
        navigate("plans/auto-upgrade/enable", { viewTransition: true });
      }
      return;
    }

    // For PAYG, delegate to inline controls component
    if (selectedOption === "payg") {
      trackBillingV2PaygChangeSubmitted({ enabled: true, source: 'limit_handling_card_inline' });
      pendingPricingActionRef.current = { kind: 'payg', enabled: true, source: 'limit_handling_card_inline' };
      paygControlsRef.current?.submit();
    }
  };

  return (
    <LoadingCard isLoading={isLoading}>
      <Card size="XL">
        <div className="flex flex-col gap-6">
          {/* Header */}
          <div className="flex flex-col gap-2">
            <h3 className="text-base font-semibold text-gray-900">
              Plan Limit Settings
            </h3>
            <p className="text-sm text-gray-600">
              Choose what to do when you reach your plan limit.
            </p>
          </div>

          {/* Options */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Interrupted Scraping - always available */}
            <RadioOption
              isSelected={selectedOption === "interrupted"}
              onClick={() => handleOptionSelect("interrupted")}
              title="Interrupted Scraping"
              description="When you hit your plan's request limit, scraping pauses until your credits renew at the start of the next billing cycle."
            />

            {/* Secondary options (auto-upgrade and/or PAYG) based on backend flags */}
            {secondaryOptions.map((option) => (
              <RadioOption
                key={option.id}
                isSelected={selectedOption === option.id}
                onClick={() => handleOptionSelect(option.id)}
                title={option.title}
                description={option.description}
                badge={option.badge}
              />
            ))}
          </div>

          {/* Inline PAYG controls — shown when PAYG is selected */}
          {selectedOption === "payg" && (
            <PayAsYouGoInlineControls
              ref={paygControlsRef}
              paygEnabled={Boolean(paygEnabled)}
              onStateChange={handlePaygStateChange}
            />
          )}

          {/* Save Button */}
          <div>
            <Button
              text={pricingOptionsFetcher?.state === "submitting" ? "SAVING..." : (hasChanges ? "SAVE CHANGES" : "CHANGES SAVED")}
              onClick={handleSaveChanges}
              className="button button-primary"
              size="SM"
              disabled={!hasChanges || (selectedOption === "payg" && !paygIsValid) || pricingOptionsFetcher?.state === "submitting"}
              loading={pricingOptionsFetcher?.state === "submitting"}
            />
          </div>
        </div>
      </Card>
    </LoadingCard>
  );
}
